import { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import { IAddEditTableForm } from "../../../../helpers/appHelpers";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import {
	useCreateBackPayGroup,
	useUpdateBackPayGroup,
} from "../../../../services/back-pay-group.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import Label from "../../../../components/form_fields/Label";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";
import APISelectField from "../../../../components/form_fields/APISelectField";
import { PayPeriodDto } from "../../../../models/pay-period.dto";
import { PATHS } from "../../../../routes/routes.paths";
import { BACKPAY_CATEGORY } from "../../../../models/back-pay.dto";

interface IFormInputs {
	name: string;
	code: string;
	description: string;
	category: BACKPAY_CATEGORY;
	startPayPeriodId: number;
	endPayPeriodId: number;
	applicablePayPeriodId: number;
}

type BackPayGroupModalFormProps = IAddEditTableForm & {
	payPeriods: PayPeriodDto[];
	reloadTableData?: () => void;
};

const BackPayGroupModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	payPeriods,
	reloadTableData,
}: BackPayGroupModalFormProps) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			name: isEditForm ? formDataValues?.name : "",
			code: isEditForm ? formDataValues?.code : "",
			description: isEditForm ? formDataValues?.description : "",
			category: isEditForm ? formDataValues?.category : "",
			startPayPeriodId: isEditForm ? formDataValues?.startPayPeriodId : "",
			endPayPeriodId: isEditForm ? formDataValues?.endPayPeriodId : "",
			applicablePayPeriodId: isEditForm
				? formDataValues?.applicablePayPeriodId
				: "",
		},
	});

	const { mutate: createBackPayGroup } = useCreateBackPayGroup();
	const { mutate: updateBackPayGroup } = useUpdateBackPayGroup();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		if (isEditForm) {
			updateBackPayGroup(
				{
					payload: data,
					companyId: currentCompany?.id as number,
					id: formDataValues?.id,
				},
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createBackPayGroup(
				{ companyId: currentCompany?.id as number, payload: data },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		reset();
		setOpen(false);
	};

	return (
		<ModalComponent
			width={775}
			open={open}
			title={title}
			handleClose={handleClose}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Name" for="name" />
						<input
							autoFocus
							placeholder={"Enter name"}
							type="text"
							{...register("name", {
								required: "Enter name here",
							})}
							id="name"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.name
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.name && (
							<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Unique Code" for="code" />
						<FormCodeInput
							register={register}
							attributes={{
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.code
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.code && (
							<p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
						)}
					</div>
					<div className="col-span-2 w-full">
						<Label title="Description" for="description" />
						<FormDescriptionInput
							register={register}
							attributes={{
								defaultValue: isEditForm ? formDataValues?.description : "",
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.description
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.description && (
							<p className="text-red-500 text-sm mt-1">
								{errors.description.message}
							</p>
						)}
					</div>
					<div className="w-full col-span-2">
						<Label title="Category" for="category" />
						<select
							{...register("category", {
								required: "Select category here",
							})}
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.category
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						>
							<option value="">Select Category</option>
							<option value="BASIC">Basic</option>
							<option value="GROSS">Gross</option>
							<option value="ALLOWANCES">Allowances</option>
							<option value="FIXED">Fixed</option>
							<option value="BASIC_DIFFERENCE">Basic Difference</option>
							<option value="GROSS_DIFFERENCE">Gross Difference</option>
						</select>
						{errors.category && (
							<p className="text-red-500 text-sm mt-1">
								{errors.category.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Applicable Pay Period" for="applicablePayPeriodId" />
						<APISelectField
							key="applicablePayPeriodId"
							attributes={{
								id: "applicablePayPeriodId",
							}}
							placeholder="Select Pay Period"
							register={register}
							name="applicablePayPeriodId"
							options={payPeriods}
							mapText="code"
							pathname={PATHS.SUB_COMPANY_SETTINGS.PAY_PERIODS}
							required="Select applicable pay period here"
							error={errors.applicablePayPeriodId}
							valueAsNumber
						/>
						{errors.applicablePayPeriodId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.applicablePayPeriodId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Start Pay Period" for="startPayPeriodId" />
						<APISelectField
							key="startPayPeriodId"
							attributes={{
								id: "startPayPeriodId",
							}}
							placeholder="Select Pay Period"
							register={register}
							name="startPayPeriodId"
							options={payPeriods}
							mapText="code"
							pathname={PATHS.SUB_COMPANY_SETTINGS.PAY_PERIODS}
							required="Select start pay period here"
							error={errors.startPayPeriodId}
							valueAsNumber
						/>
						{errors.startPayPeriodId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.startPayPeriodId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="End Pay Period" for="endPayPeriodId" />
						<APISelectField
							key="endPayPeriodId"
							attributes={{
								id: "endPayPeriodId",
							}}
							placeholder="Select Pay Period"
							register={register}
							name="endPayPeriodId"
							options={payPeriods}
							mapText="code"
							pathname={PATHS.SUB_COMPANY_SETTINGS.PAY_PERIODS}
							required="Select end pay period here"
							error={errors.endPayPeriodId}
							valueAsNumber
						/>
						{errors.endPayPeriodId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.endPayPeriodId.message}
							</p>
						)}
					</div>
				</div>
				<div className="w-full flex justify-end mt-12">
					<OrangeButton
						type="submit"
						title={
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Back Pay Group"
									: "Add Back Pay Group"
						}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default BackPayGroupModalForm;
